/**
 * COMPRADO CONJUNTAMENTE (filas)
 *
 * Cada fila tiene una casilla; el botón de abajo añade al carrito todo lo marcado en una sola
 * llamada. La talla del producto de la ficha la manda el selector de arriba (input oculto
 * `[data-variant-id]` del formulario); la de los complementarios, su propio desplegable.
 *
 * Descuento por conjunto: si el bloque trae `data-discount-pct` > 0 y están marcadas TODAS las
 * filas, el total se enseña rebajado y, al añadir, se aplica `data-discount-code` al carrito con
 * `cart/update.js` (el código debe existir en Descuentos con ese mismo %). Si el cliente desmarca
 * algo, no hay rebaja y no se toca ningún código.
 */
class BoughtTogetherRows extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.form = this.closest('form') || document.querySelector('[data-pdp-form]');
    this.current = this.querySelector('[data-btr-current]');
    const json = this.current?.querySelector('[data-btr-variants]');
    this.variants = json ? JSON.parse(json.textContent) : {};

    this.addEventListener('change', () => this.refresh());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-btr-submit]')) this.submit();
    });
    // El selector de talla de arriba actualiza el input oculto sin avisar: se relee tras cada gesto.
    ['click', 'change'].forEach((type) =>
      this.form?.addEventListener(type, () => setTimeout(() => this.refresh(), 120))
    );

    this.refresh();
  }

  get rows() {
    return Array.from(this.querySelectorAll('[data-btr-row]'));
  }

  refresh() {
    const money = (cents) => this.formatMoney(cents);
    let total = 0;
    let checked = 0;
    let blocked = false;

    for (const row of this.rows) {
      if (row === this.current) {
        const id = this.form?.querySelector('[data-variant-id]')?.value;
        const variant = this.variants[id];
        if (variant) {
          row.dataset.variantId = id;
          row.dataset.price = variant.price;
          row.dataset.available = variant.available;
          row.querySelector('[data-btr-price]').textContent = variant.formatted;
          const label = row.querySelector('[data-btr-variant-title]');
          if (label) label.textContent = variant.title === 'Default Title' ? '' : variant.title;
        }
      } else {
        const select = row.querySelector('[data-btr-select]');
        if (select) {
          const option = select.selectedOptions[0];
          row.dataset.variantId = select.value;
          row.dataset.price = option?.dataset.price ?? row.dataset.price;
          row.querySelector('[data-btr-price]').textContent = option?.dataset.priceFormatted ?? '';
        }
      }

      const on = row.querySelector('[data-btr-check]').checked;
      row.classList.toggle('is-on', on);
      if (!on) continue;

      checked += 1;
      total += Number(row.dataset.price || 0);
      if (row.dataset.available === 'false') blocked = true;
    }

    const pct = Number(this.dataset.discountPct || 0);
    const allOn = checked > 1 && checked === this.rows.length;
    this.dealActive = pct > 0 && allOn;

    const totalEl = this.querySelector('[data-btr-total]');
    const wasEl = this.querySelector('[data-btr-was]');
    const hintEl = this.querySelector('[data-btr-hint]');
    const savingEl = this.querySelector('[data-btr-saving]');

    if (this.dealActive) {
      const discounted = Math.round((total * (100 - pct)) / 100);
      totalEl.textContent = money(discounted);
      if (wasEl) {
        wasEl.textContent = money(total);
        wasEl.hidden = false;
      }
      if (hintEl) hintEl.hidden = true;
      if (savingEl) {
        savingEl.textContent = (this.dataset.savingText || '')
          .replace('[saving]', money(total - discounted))
          .replace('[pct]', String(pct));
        savingEl.hidden = !savingEl.textContent;
      }
    } else {
      totalEl.textContent = money(total);
      if (wasEl) wasEl.hidden = true;
      if (hintEl) hintEl.hidden = false;
      if (savingEl) savingEl.hidden = true;
    }

    this.querySelector('[data-btr-submit]').disabled = checked === 0 || blocked;
  }

  async submit() {
    const button = this.querySelector('[data-btr-submit]');
    if (button.getAttribute('aria-busy') === 'true') return;

    // Propiedades ocultas (empiezan por «_», el cliente no las ve en carrito, checkout ni correos) para
    // saber en el pedido qué se compró con este bloque: `_conjunto` = handle del producto de la ficha y
    // `_conjunto_completo` = si estaban marcadas todas las filas (es cuando aplica el descuento).
    const checkedRows = this.rows.filter((row) => row.querySelector('[data-btr-check]').checked);
    const properties = {
      _conjunto: this.dataset.productHandle || 'si',
      _conjunto_completo: checkedRows.length === this.rows.length ? 'si' : 'no',
    };
    const items = checkedRows.map((row) => ({
      id: Number(row.dataset.variantId),
      quantity: 1,
      properties,
    }));
    if (!items.length) return;
    // El carrito enseña lo último añadido arriba: se añade en orden inverso para que, de arriba a
    // abajo, queden en el mismo orden que las filas (el producto de la ficha primero).
    items.reverse();

    button.setAttribute('aria-busy', 'true');
    this.showError('');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const json = await response.json();
      if (!response.ok) {
        this.showError(json.description || json.message || this.dataset.errorText || 'No se ha podido añadir.');
        return;
      }

      await this.applyDiscount(root);

      document.dispatchEvent(new CustomEvent('cart:updated'));
      await this.openCart();
    } catch (error) {
      this.showError(error?.message);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** Aplica el código del conjunto solo cuando se ha añadido el conjunto completo. */
  async applyDiscount(root) {
    const code = (this.dataset.discountCode || '').trim();
    if (!this.dealActive || !code) return;
    try {
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
    } catch (_error) {
      /* si el código no entra, el carrito sigue bien: solo se pierde la rebaja */
    }
  }

  async openCart() {
    if (typeof window.pompeiiOpenUpcart === 'function') {
      window.pompeiiOpenUpcart();
      return;
    }
    if (
      typeof window.upcartOpenCart === 'function' &&
      window.upcartSettings?.upcartEditorSettings?.cartIsEnabled !== false
    ) {
      document.body.classList.remove('cart-drawer-open');
      window.upcartOpenCart();
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (typeof drawer.refresh === 'function') await drawer.refresh();
      if (typeof drawer.open === 'function') drawer.open();
    }
  }

  showError(message) {
    const box = this.querySelector('[data-btr-error]');
    box.textContent = message || '';
    box.hidden = !message;
  }

  /** Formato de la tienda (`{{amount_with_comma_separator}}€`…): solo hace falta para la suma. */
  formatMoney(cents) {
    const format = this.getAttribute('money-format') || '{{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_m, name) => {
      const decimals = name.includes('no_decimals') ? 0 : 2;
      let thousands = ',';
      let decimal = '.';
      if (name.includes('comma_separator')) {
        thousands = '.';
        decimal = ',';
      } else if (name.includes('apostrophe_separator')) {
        thousands = "'";
      } else if (name.includes('space_separator')) {
        thousands = ' ';
        if (!name.includes('period_and_space')) decimal = ',';
      }
      const [integer, fraction] = (cents / 100).toFixed(decimals).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? `${grouped}${decimal}${fraction}` : grouped;
    });
  }
}

if (!window.customElements.get('bought-together-rows')) {
  window.customElements.define('bought-together-rows', BoughtTogetherRows);
}
